import React, { useState } from 'react';
import {
  Box, Typography, Button, Paper, TextField, Chip, IconButton,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';

const BreakdownView = ({ project, onRefresh, onNext, onBack }) => {
  const [scenes, setScenes] = useState(project.scenes || []);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const updateScene = (idx, field, value) => {
    const updated = [...scenes];
    updated[idx] = { ...updated[idx], [field]: value };
    setScenes(updated);
  };

  const addChip = (idx, field, value) => {
    if (!value.trim()) return;
    const updated = [...scenes];
    const current = updated[idx][field] || [];
    if (!current.includes(value.trim())) {
      updated[idx] = { ...updated[idx], [field]: [...current, value.trim()] };
      setScenes(updated);
    }
  };

  const removeChip = (idx, field, chipIdx) => {
    const updated = [...scenes];
    const current = [...(updated[idx][field] || [])];
    current.splice(chipIdx, 1);
    updated[idx] = { ...updated[idx], [field]: current };
    setScenes(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects/${project.id}/scenes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scenes),
      });
      if (!resp.ok) throw new Error('Opslaan mislukt');
      toast.success('Scenes opgeslagen');
      onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (!(await bevestig({ titel: 'Breakdown overschrijven?', tekst: 'De bestaande breakdown wordt vervangen door een nieuwe AI-generatie.', knop: 'Overschrijven' }))) return;
    setRegenerating(true);
    try {
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects/${project.id}/generate-breakdown`, {
        method: 'POST',
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.detail || 'Generatie mislukt');
      }
      const data = await resp.json();
      toast.success(`${data.scenes_created} scenes gegenereerd`);
      onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRegenerating(false);
    }
  };

  const handleGenerateShotlist = async () => {
    await handleSave();
    onNext();
  };

  if (scenes.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary" gutterBottom>
          Nog geen scenes. Ga terug naar stap 1 om een breakdown te genereren.
        </Typography>
        <Button startIcon={<NavigateBeforeIcon />} onClick={onBack}>Terug naar script</Button>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Scene Breakdown ({scenes.length} scenes)
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={regenerating ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
            onClick={handleRegenerate} disabled={regenerating}>
            Opnieuw genereren
          </Button>
          <Button variant="outlined" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave} disabled={saving}>
            Opslaan
          </Button>
        </Box>
      </Box>

      {scenes.map((scene, idx) => (
        <Accordion key={scene.id || idx} defaultExpanded={idx === 0} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Chip label={`Scene ${scene.scene_number}`} size="small" color="primary" />
              <Typography fontWeight={600} sx={{ flexGrow: 1 }}>
                {scene.heading || `Scene ${scene.scene_number}`}
              </Typography>
              {scene.int_ext && <Chip label={scene.int_ext} size="small" variant="outlined" />}
              {scene.location && (
                <Chip label={scene.location} size="small"
                  sx={{ bgcolor: 'rgba(124,58,237,0.1)', color: '#7c3aed' }} />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <TextField size="small" label="Scene heading" fullWidth
                value={scene.heading || ''} onChange={(e) => updateScene(idx, 'heading', e.target.value)} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField size="small" label="INT/EXT" sx={{ width: 100 }}
                  value={scene.int_ext || ''} onChange={(e) => updateScene(idx, 'int_ext', e.target.value)} />
                <TextField size="small" label="Locatie" fullWidth
                  value={scene.location || ''} onChange={(e) => updateScene(idx, 'location', e.target.value)} />
                <TextField size="small" label="Tijd" sx={{ width: 120 }}
                  value={scene.time_of_day || ''} onChange={(e) => updateScene(idx, 'time_of_day', e.target.value)} />
              </Box>
            </Box>

            <TextField size="small" label="Beschrijving" fullWidth multiline minRows={2}
              value={scene.description || ''} onChange={(e) => updateScene(idx, 'description', e.target.value)}
              sx={{ mb: 2 }} />

            {/* Characters chips */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Personages
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                {(scene.characters || []).map((c, ci) => (
                  <Chip key={ci} label={c} size="small" color="info" variant="outlined"
                    onDelete={() => removeChip(idx, 'characters', ci)} />
                ))}
                <ChipInput onAdd={(val) => addChip(idx, 'characters', val)} placeholder="+ personage" />
              </Box>
            </Box>

            {/* Props chips */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Props / Rekwisieten
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                {(scene.props || []).map((p, pi) => (
                  <Chip key={pi} label={p} size="small" color="warning" variant="outlined"
                    onDelete={() => removeChip(idx, 'props', pi)} />
                ))}
                <ChipInput onAdd={(val) => addChip(idx, 'props', val)} placeholder="+ prop" />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button startIcon={<NavigateBeforeIcon />} onClick={onBack}>Terug</Button>
        <Button variant="contained" endIcon={<NavigateNextIcon />} onClick={handleGenerateShotlist}
          sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
          Verder naar shotlist
        </Button>
      </Box>
    </Box>
  );
};

// Small inline input for adding chips
const ChipInput = ({ onAdd, placeholder }) => {
  const [value, setValue] = useState('');

  const handleSubmit = () => {
    if (value.trim()) {
      onAdd(value);
      setValue('');
    }
  };

  return (
    <TextField
      size="small"
      variant="standard"
      placeholder={placeholder}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); } }}
      onBlur={handleSubmit}
      sx={{ width: 120, '& .MuiInput-input': { fontSize: 13 } }}
      InputProps={{
        endAdornment: value ? (
          <IconButton size="small" onClick={handleSubmit}><AddIcon sx={{ fontSize: 16 }} /></IconButton>
        ) : null,
      }}
    />
  );
};

export default BreakdownView;
